/**
 * Draggable column widths for the page editor (blocks rail | form | preview). Widths are
 * pixels stored in localStorage; the middle column always takes the remaining space.
 * Handles are keyboard operable (arrow keys, Home/End) and double-click resets a column.
 */
import { cn } from "@aurumiq/ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "aurumiq.cms.editor.columns";
const STEP = 16;

export interface ColumnSizes {
  rail: number;
  preview: number;
}

export const DEFAULT_SIZES: ColumnSizes = { rail: 240, preview: 520 };
export const LIMITS: Record<keyof ColumnSizes, { min: number; max: number }> = {
  rail: { min: 160, max: 480 },
  preview: { min: 320, max: 1200 },
};

const clamp = (k: keyof ColumnSizes, v: number) =>
  Math.round(Math.min(LIMITS[k].max, Math.max(LIMITS[k].min, v)));

function load(): ColumnSizes {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return DEFAULT_SIZES;
    const p = JSON.parse(v) as Partial<ColumnSizes>;
    return {
      rail: clamp("rail", Number(p.rail) || DEFAULT_SIZES.rail),
      preview: clamp("preview", Number(p.preview) || DEFAULT_SIZES.preview),
    };
  } catch {
    return DEFAULT_SIZES;
  }
}

export function useColumnSizes() {
  const [sizes, setSizes] = useState<ColumnSizes>(load);
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(sizes));
    } catch {
      /* ignore */
    }
  }, [sizes]);
  const set = useCallback(
    (k: keyof ColumnSizes, v: number) => setSizes((s) => ({ ...s, [k]: clamp(k, v) })),
    [],
  );
  const reset = useCallback(
    (k: keyof ColumnSizes) => setSizes((s) => ({ ...s, [k]: DEFAULT_SIZES[k] })),
    [],
  );
  return { sizes, set, reset };
}

/**
 * Vertical splitter. `grow` is the drag direction that makes the column wider:
 * the rail sits left of its handle (+x grows), the preview right of its handle (−x grows).
 */
export function ResizeHandle({
  column,
  value,
  grow,
  onChange,
  onReset,
  label,
}: {
  column: keyof ColumnSizes;
  value: number;
  grow: "right" | "left";
  onChange: (v: number) => void;
  onReset: () => void;
  label: string;
}) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; value: number } | null>(null);
  const sign = grow === "right" ? 1 : -1;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    start.current = { x: e.clientX, value };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    onChange(start.current.value + sign * (e.clientX - start.current.x));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    start.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const big = e.shiftKey ? STEP * 4 : STEP;
    const delta =
      e.key === "ArrowRight"
        ? big
        : e.key === "ArrowLeft"
          ? -big
          : e.key === "Home"
            ? -Infinity
            : e.key === "End"
              ? Infinity
              : 0;
    if (delta === 0) return;
    e.preventDefault();
    onChange(
      Number.isFinite(delta)
        ? value + sign * delta
        : delta * sign > 0
          ? LIMITS[column].max
          : LIMITS[column].min,
    );
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: a separator with aria-valuenow is the ARIA pattern for a splitter
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={LIMITS[column].min}
      aria-valuemax={LIMITS[column].max}
      aria-valuenow={value}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      className={cn(
        "group hidden w-2 cursor-col-resize touch-none select-none items-stretch justify-center rounded outline-none lg:flex",
        "focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <span
        className={cn(
          "w-px bg-border transition-colors duration-150 group-hover:bg-primary",
          dragging && "bg-primary",
        )}
      />
    </div>
  );
}
